import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import queryString from 'query-string';

import clsx from 'clsx';

import { get, isEmpty, startCase, orderBy } from 'lodash';

import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import GoogleLogo from '../../../../components/GoogleLogo';
import InputAdornment from '@material-ui/core/InputAdornment';
import AutoComplete from '@material-ui/lab/Autocomplete';

import BobConfirmButton from '../../../../components/BobConfirmButton';
import BobCancelButton from '../../../../components/BobCancelButton';

import * as ArchitectActions from '../../../../actions/architectActions';
import * as ContactActions from '../../../../actions/contactActions';
import * as K2ACoreActions from '../../../../actions/k2aCoreActions';

import * as classes from './ManageClientPage.module.css';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const ValidationSchema = Yup.object().shape({
    clientName: Yup.string()
        .required(),
    businessType: Yup.string()
        .required(),
    addressSearch: Yup.string()
        .required(),
    primaryContact: Yup.string()
});

const ManageClientPage = ({
    activeNavCompany,
    businessTypes,
    createCoreClient,
    clientContacts,
    contacts,
    customAttributes,
    fetchClientContacts,
    fetchContacts,
    fetchCoreClientById,
    history,
    selectedClient,
    setCoreClient,
    setCoreClients,
    sources,
    updateCoreClient,
    ...props
}) => {
    const [address, setAddress] = useState({});
    const [clientAttributes, setClientAttributes] = useState([]);

    const [address1, setAddress1] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zip, setZip] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);
    const [activeCustomAttributes, setActiveCustomAttributes] = useState({});

    useEffect(() => {
        return () => {
            const { pathname } = history.location;
            let updatedUrl = history.location.search.replace(/[?&]entityType=[^&]+/, '').replace(/^&/, '?');
            updatedUrl = updatedUrl.replace(/[?&]entityId=[^&]+/, '').replace(/^&/, '?');
            setCoreClient({});
            history.push({ pathname, search: updatedUrl });
        };
    }, []);

    useEffect(() => {
        const init = async () => {
            if (process.env.contactsEnabled) {
                await fetchClientContacts(selectedClient.id);
            } else {
                await fetchContacts(selectedClient.id);
            }
        };
        
        if (selectedClient.id) init();

        if (!isEmpty(selectedClient)) {
            formik.setFieldValue('clientName', selectedClient.name, false);
            formik.setFieldValue('businessType', selectedClient.businessType.id, false);
            formik.setFieldValue('addressSearch', selectedClient.address?.formattedAddress, false);
            formik.setFieldValue('primaryContact', selectedClient.primaryContact.id === EMPTY_GUID ? '' : selectedClient.primaryContact.id, false);
            if (!isEmpty(selectedClient.customAttributes)) {
                let attributeObject = {};
                selectedClient.customAttributes.forEach(item => {
                    attributeObject[item.customAvp.attributeName] = { id: item.customAvp.avpId, value: item.customAvp.attributeValue };
                });
                setActiveCustomAttributes(attributeObject);
            }
        }

    }, [selectedClient]);

    useEffect(() => {
        const init = async () => {
            const queryValues = queryString.parse(history.location.search);
            if (queryValues.entityId) {
                if (isEmpty(selectedClient) || selectedClient.id !== queryValues.entityId) {
                    await fetchCoreClientById(queryValues.entityId);
                }
            }
        };

        if (activeNavCompany.id) init();
    }, [activeNavCompany]);

    useEffect(() => {
        if (!isEmpty(customAttributes)) {
            setClientAttributes(customAttributes.Client);
            setIsInitializing(false);
        } else {
            setIsInitializing(true);
        }
    }, [customAttributes]);

    const handleRoute = () => {
        const path = '/v2/dashboard/core/entities';
        if (history.location.state && history.location.state.returnUrl) {
            history.push(history.location.state.returnUrl);
        } else {
            history.push({ pathname: path, search: history.location.search });
        }
    };

    const formik = useFormik({
        initialValues: {
            clientName:     get(selectedClient, 'name', ''),
            businessType:   get(selectedClient, 'businessType.id', ''),
            primaryContact: get(selectedClient, 'primaryContact.id', '') === EMPTY_GUID ? '' : get(selectedClient, 'primaryContact.id', ''),
            addressSearch:  get(selectedClient, 'address.formattedAddress', '')
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const queryValues = queryString.parse(history.location.search);

            const body = {
                name:           values.clientName,
                address,
                businessTypeId: values.businessType
            };

            if (values.primaryContact) body.primaryContactId = values.primaryContact;

            if (!isEmpty(activeCustomAttributes)) {
                body.customAttributes = {
                    customAttributeValuePairs: []
                };

                const source = sources.find(item => item.name === 'K2A Core');
                const keys = Object.keys(activeCustomAttributes);

                keys.forEach(item => {
                    if (!isEmpty(activeCustomAttributes[item])) {
                        body.customAttributes.customAttributeValuePairs.push({
                            sourceAvp: { avpId: source.id },
                            customAvp: { avpId: activeCustomAttributes[item].id }
                        });
                    }
                });
            }

            const isUpdate = !isEmpty(queryValues.entityId);
            setIsLoading(true);
            let isCreated;
            try {
                if (isUpdate) {
                    isCreated = await updateCoreClient(queryValues.entityId, body);
                } else {
                    isCreated = await createCoreClient(activeNavCompany.id, body);
                }
                setIsLoading(false);
                if (isCreated) {
                    setCoreClients([], {});
                    handleRoute();
                }
            } catch (error) {
                setIsLoading(false);
            }
        }
    });

    const handleGeocodeAddress = async address => {
        try {
            const results = await geocodeByAddress(address);
            const latLng = await getLatLng(results[0]);

            const streetNumber = results[0].address_components.find(result => {
                return result.types.includes('street_number');
            });

            const route = results[0].address_components.find(result => {
                return result.types.includes('route');
            });

            const city = results[0].address_components.find(result => {
                return result.types.includes('locality');
            });

            const subLocality = results[0].address_components.find(result => {
                return result.types.includes('sublocality');
            });

            const administrativeAreaLevel2 = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_2');
            });

            const administrativeAreaLevel3 = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_3');
            });

            const state = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_1');
            });

            const zip = results[0].address_components.find(result => {
                return result.types.includes('postal_code');
            });

            const country = results[0].address_components.find(result => {
                return result.types.includes('country');
            });

            const address1Check = streetNumber ? `${streetNumber.long_name} ${route.long_name}` : route.long_name;
            const cityCheck = city ? city.long_name : administrativeAreaLevel3 ? administrativeAreaLevel3.long_name  : subLocality ? subLocality.long_name : '';

            formik.setFieldValue('addressSearch', results[0].formatted_address);

            setAddress1(address1Check);
            setCity(cityCheck);
            setState(state.short_name);
            setZip(zip.long_name);

            setAddress({
                latitude:                 get(latLng, 'lat', ''),
                longitude:                get(latLng, 'lng', ''),
                administrativeAreaLevel1: get(state, 'short_name', ''),
                administrativeAreaLevel2: get(administrativeAreaLevel2, 'long_name', ''),
                country:                  get(country, 'short_name', ''),
                locality:                 cityCheck,
                postalCode:               get(zip, 'long_name', ''),
                route:                    get(route, 'long_name', ''),
                streetNumber:             streetNumber ? streetNumber.long_name : '',
                formattedAddress:         get(results[0], 'formatted_address', '')
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Error: ', error);
        }
    };

    if (!isEmpty(formik.values.addressSearch) && isEmpty(address1)) {
        handleGeocodeAddress(formik.values.addressSearch);
    }

    const handleClearField = () => {
        formik.setFieldValue('primaryContact', '');
    };

    const handleChangeCustomAttribute = (event, value, attribute) => {
        let updatedAttributes = { ...activeCustomAttributes };
        updatedAttributes[attribute] = value;
        setActiveCustomAttributes(updatedAttributes);
    };
    
    return (
        <div className={isInitializing ? classes.Loading : classes.ManageClientPage}>
            {
                isInitializing ?
                    <div className={classes.Loader}>
                        <img src="/assets/images/BobLoaderLarge.gif" alt="loader" />
                    </div>
                    :
                    <React.Fragment>
                        <div className={classes.ManagePageTitle}>
                            Client Details
                        </div>
                        <div className={classes.ManagePageBody}>
                            <FormControl className={classes.inputWrapper}>
                                <TextField
                                    id="clientName"
                                    name="clientName"
                                    type="text"
                                    value={formik.values.clientName}
                                    label={<span>Client Name<span className={classes.required}> *</span></span>}
                                    placeholder="Enter"
                                    size="small"
                                    onChange={formik.handleChange}
                                    InputLabelProps={{
                                        classes: {
                                            error:   classes.error,
                                            focused: classes.labelFocused,
                                            root:    classes.label
                                        }
                                    }}
                                    InputProps={{
                                        onBlur:  formik.handleBlur,
                                        classes: {
                                            error:     classes.cssErrorUnderline,
                                            underline: classes.cssUnderline,
                                            root:      classes.input
                                        }
                                    }}
                                    variant="outlined"
                                    FormHelperTextProps={{
                                        classes: {
                                            error: classes.error
                                        }
                                    }}
                                    error={Boolean(formik.errors.clientName) && Boolean(formik.touched.clientName)}
                                    helperText={Boolean(formik.touched.clientName) && formik.errors.clientName}
                                />
                            </FormControl>
                            <FormControl variant="outlined" size="small" className={classes.inputWrapper}>
                                <InputLabel
                                    htmlFor="businessType"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.businessType) && Boolean(formik.touched.businessType)}
                                >
                                    {<span>Business Type<span className={classes.required}> *</span></span>}
                                </InputLabel>
                                <Select
                                    value={formik.values.businessType}
                                    id="businessType"
                                    onChange={formik.handleChange}
                                    name="businessType"
                                    classes={{
                                        root: classes.input
                                    }}
                                    inputProps={{
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.businessType) && Boolean(formik.touched.businessType)}
                                >
                                    {
                                        businessTypes.map(type =>
                                            <MenuItem
                                                key={type.id}
                                                value={type.id}
                                            >
                                                {type.name}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.businessType &&
                                    formik.errors.businessType && <FormHelperText className={classes.error}>{formik.errors.businessType}</FormHelperText>}
                            </FormControl>
                            {
                                !isEmpty(selectedClient) ?
                                    <FormControl variant="outlined" size="small" className={classes.inputWrapper}>
                                        <InputLabel
                                            htmlFor="primaryContact"
                                            classes={{
                                                error:   classes.error,
                                                focused: classes.labelFocused,
                                                root:    classes.label
                                            }}
                                            error={Boolean(formik.errors.primaryContact) && Boolean(formik.touched.primaryContact)}
                                        >
                                            Primary Contact
                                        </InputLabel>
                                        <Select
                                            value={formik.values.primaryContact}
                                            id="primaryContact"
                                            onChange={formik.handleChange}
                                            name="primaryContact"
                                            classes={{
                                                root: classes.input
                                            }}
                                            endAdornment={
                                                !isEmpty(formik.values.primaryContact) ?
                                                    <InputAdornment position="end">
                                                        <span role="none" onClick={() => handleClearField()} className={clsx('material-icons-outlined md-18', classes.cancelIcon)}>
                                                            close
                                                        </span>
                                                    </InputAdornment>
                                                    :
                                                    null
                                            }
                                            inputProps={{
                                                onBlur: formik.handleBlur
                                            }}
                                            error={Boolean(formik.errors.primaryContact) && Boolean(formik.touched.primaryContact)}
                                        >
                                            {
                                                process.env.contactsEnabled ?
                                                    isEmpty(clientContacts) ? null :
                                                        clientContacts.map(contact =>
                                                            <MenuItem
                                                                key={contact.id}
                                                                value={contact.id}
                                                            >
                                                                {`${contact.userName.FirstName} ${contact.userName.LastName}`}
                                                            </MenuItem>
                                                        )
                                                    :
                                                    isEmpty(contacts) ? null :
                                                        contacts.map(contact =>
                                                            <MenuItem
                                                                key={contact.id}
                                                                value={contact.id}
                                                            >
                                                                {contact.name}
                                                            </MenuItem>
                                                        )
                                            }
                                        </Select>
                                        {formik.touched.primaryContact &&
                                            formik.errors.primaryContact && <FormHelperText className={classes.error}>{formik.errors.primaryContact}</FormHelperText>}
                                    </FormControl>
                                    :
                                    null
                            }
                            <PlacesAutocomplete
                                value={formik.values.addressSearch}
                                onChange={address => formik.setFieldValue('addressSearch', address)}
                                onSelect={(handleGeocodeAddress)}
                            >
                                {({ getInputProps, suggestions, getSuggestionItemProps }) => (
                                    <React.Fragment>
                                        <FormControl variant="outlined" size="small" className={classes.inputWrapper}> 
                                            <TextField
                                                {...getInputProps({
                                                    name: 'addressSearch',
                                                    id:   'addressSearch'
                                                })}
                                                label={<span>Search for Address<span className={classes.required}> *</span></span>}
                                                variant="outlined"
                                                size="small"
                                                classes={{
                                                    error:     classes.cssErrorUnderline,
                                                    underline: classes.cssUnderline,
                                                    root:      classes.input
                                                }}
                                                error={Boolean(formik.errors.addressSearch) && Boolean(formik.touched.addressSearch)}
                                            />
                                            {formik.touched.addressSearch &&
                                                formik.errors.addressSearch && <FormHelperText className={classes.error}>{formik.errors.addressSearch}</FormHelperText>}
                                        </FormControl>
                                        {suggestions.length > 0 && <Paper className={classes.autoCompleteList}>
                                            <List>
                                                {suggestions.map((suggestion, index) => {
                                                    return (
                                                        <div key={index}>
                                                            <ListItem
                                                                button
                                                                className={classes.autoCompleteListItem}
                                                                {...getSuggestionItemProps(suggestion)}
                                                            >
                                                                <ListItemText primary={suggestion.description} />
                                                            </ListItem>
                                                        </div>
                                                    );
                                                })}
                                            </List>
                                            <GoogleLogo />
                                        </Paper>}
                                    </React.Fragment>
                                )}
                            </PlacesAutocomplete>
                            <div className={classes.HelperLine}>
                                <span className={clsx('material-icons-outlined md-18', classes.HelperIcon)}>
                                    info
                                </span>
                                <span>Enter the physical address used to fill the address fields below.</span>
                            </div>
                            <FormControl className={classes.inputWrapper}>
                                <TextField
                                    id="address1"
                                    name="address1"
                                    type="text"
                                    value={address1}
                                    label="Address"
                                    disabled
                                    size="small"
                                    InputLabelProps={{
                                        classes: {
                                            error:   classes.error,
                                            focused: classes.labelFocused,
                                            root:    classes.label
                                        }
                                    }}
                                    variant="outlined"
                                    InputProps={{
                                        classes: {
                                            error:     classes.cssErrorUnderline,
                                            underline: classes.cssUnderline,
                                            root:      classes.input
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormControl className={classes.inputWrapper}>
                                <TextField
                                    id="city"
                                    name="city"
                                    type="text"
                                    value={city}
                                    label="City"
                                    disabled
                                    size="small"
                                    InputLabelProps={{
                                        classes: {
                                            error:   classes.error,
                                            focused: classes.labelFocused,
                                            root:    classes.label
                                        }
                                    }}
                                    variant="outlined"
                                    InputProps={{
                                        classes: {
                                            error:     classes.cssErrorUnderline,
                                            underline: classes.cssUnderline,
                                            root:      classes.input
                                        }
                                    }}
                                />
                            </FormControl>
                            <div className={classes.formRow}>
                                <FormControl className={clsx(classes.inputWrapper, classes.state)}>
                                    <TextField
                                        id="state"
                                        name="state"
                                        type="text"
                                        value={state}
                                        label="State"
                                        disabled
                                        size="small"
                                        InputLabelProps={{
                                            classes: {
                                                error:   classes.error,
                                                focused: classes.labelFocused,
                                                root:    classes.label
                                            }
                                        }}
                                        variant="outlined"
                                        InputProps={{
                                            classes: {
                                                error:     classes.cssErrorUnderline,
                                                underline: classes.cssUnderline,
                                                root:      classes.input
                                            }
                                        }}
                                    />
                                </FormControl>
                                <FormControl className={clsx(classes.inputWrapper, classes.zip)}>
                                    <TextField
                                        id="zip"
                                        name="zip"
                                        type="text"
                                        value={zip}
                                        label="Zip Code"
                                        disabled
                                        size="small"
                                        InputLabelProps={{
                                            classes: {
                                                error:   classes.error,
                                                focused: classes.labelFocused,
                                                root:    classes.label
                                            }
                                        }}
                                        variant="outlined"
                                        InputProps={{
                                            classes: {
                                                error:     classes.cssErrorUnderline,
                                                underline: classes.cssUnderline,
                                                root:      classes.input
                                            }
                                        }}
                                    />
                                </FormControl>
                            </div>
                        </div>
                        <div className={classes.ManagePageAttributes}>
                            {
                                clientAttributes.filter(ca => ca.name !== 'activeState' && ca.name !== 'externalId').map(item => {
                                    return (
                                        <FormControl className={classes.inputWrapper}>
                                            <AutoComplete
                                                id={item.name}
                                                name={item.name}
                                                options={orderBy(item.avpLookupValues, [lv => lv.value.toLowerCase()], 'asc')}
                                                getOptionLabel={(option) => option.value || ''}
                                                value={!isEmpty(activeCustomAttributes) ? activeCustomAttributes[item.name] : null}
                                                getOptionSelected={(option, value) => option === value}
                                                onChange={(event, value) => handleChangeCustomAttribute(event, value, item.name)}
                                                size="small"
                                                renderInput={(params) =>
                                                    <TextField
                                                        {...params}
                                                        className={classes.Required}
                                                        label={startCase(item.name)}
                                                        variant="outlined"
                                                        InputProps={{
                                                            ...params.InputProps
                                                        }}
                                                    />}
                                            />
                                        </FormControl>
                                    );
                                })
                            }
                        </div>
                        <div className={classes.ManagePageButtons}>
                            <BobCancelButton
                                onClick={handleRoute}
                                disabled={formik.isSubmitting}
                                className={classes.CancelButton}
                                label={'Cancel'}
                            />
                            <BobConfirmButton
                                onClick={formik.handleSubmit}
                                disabled={formik.isSubmitting}
                                className={isLoading ? classes.DisabledButtons : classes.Buttons}
                                isLoading={isLoading}
                                label={'Save'}
                                iconSize={15}
                            />
                        </div>
                    </React.Fragment>
            }
            
        </div>
    );
};

const mapStateToProps = state => {
    return {
        activeNavCompany: state.bob.activeNavCompany,
        businessTypes:    state.architect.businessTypes,
        clientContacts:   state.contact.clientContacts,
        contacts:         state.architect.contacts,
        customAttributes: state.attributeManagement.customAttributes,
        selectedClient:   state.k2aCore.selectedClient,
        sources:          state.attributeManagement.sources
    };
};

const mapDispatchToProps = {
    ...K2ACoreActions,
    ...ArchitectActions,
    ...ContactActions
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ManageClientPage));
